import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { orderAPI } from '../../api';
import { formatApiError } from '../../lib/apiError';

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [paymentError, setPaymentError] = useState('');

  useEffect(() => {
    if (!orderId) return undefined;
    let active = true;
    const refresh = () => orderAPI.getOrder(orderId).then(res => {
      if (!active) return;
      const current = res.data.order;
      setOrder(current);
      // This order is recoverable by its ID; a future cart needs a new operation key.
      sessionStorage.removeItem('petals-checkout-key');
    }).catch(() => {});
    refresh();
    const interval = window.setInterval(refresh, 5000);
    return () => { active = false; window.clearInterval(interval); };
  }, [orderId]);

  if (!order) return <div className="pw-container py-16 text-center"><p>Loading...</p></div>;

  return (
    <div className="pw-container py-12">
      <div className="max-w-2xl mx-auto text-center">
        <CheckCircle size={64} className="mx-auto text-[#2f6b4f] mb-4" />
        <h1 className="text-2xl font-semibold mb-2">Order received</h1>
        <p className="text-[#6f6a63] mb-6">Payment status: {order.payment_status || 'pending'}. An order receipt does not confirm payment.</p>
        
        <div className="bg-[#fbfaf8] border border-[#e7e3dd] rounded-xl p-6 text-left mb-6">
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div><span className="text-[#6f6a63]">Order Number:</span><br /><span className="font-semibold">{order.order_number}</span></div>
            <div><span className="text-[#6f6a63]">Status:</span><br /><span className="font-semibold capitalize">{order.status}</span></div>
            <div><span className="text-[#6f6a63]">Payment:</span><br /><span className="font-semibold capitalize">{order.payment_method?.replace('_', ' ')}</span></div>
            <div><span className="text-[#6f6a63]">Total:</span><br /><span className="font-semibold text-lg">${order.total?.toFixed(2)}</span></div>
          </div>

          <div className="mt-4 border-t border-[#e7e3dd] pt-4">
            <h3 className="text-xs tracking-wider uppercase text-[#6f6a63] mb-2">Items</h3>
            {order.items?.map((item, i) => (
              <div key={i} className="flex justify-between text-sm py-1">
                <span>{item.product_name} x{item.quantity}</span>
                <span>${item.line_total?.toFixed(2)}</span>
              </div>
            ))}
          </div>

          {order.payment_method === 'bank_transfer' && (
            <div className="mt-4 bg-[#e7f1ea] rounded-lg p-4 text-sm">
              <p className="font-medium text-[#2f6b4f]">Bank Transfer Instructions</p>
              <p className="text-[#6f6a63] mt-1">Your order is awaiting payment confirmation. Please use only bank details provided directly by Petals West and reference {order.order_number}.</p>
            </div>
          )}
          {order.payment_method === 'stripe' && order.payment_status === 'pending' && (
            <button type="button" className="mt-4 bg-[#2f6b4f] text-white px-5 py-3 rounded-lg text-sm" onClick={() => {
              orderAPI.resumeStripeCheckout(orderId).then(res => window.location.assign(res.data.checkout_url))
                .catch(err => setPaymentError(formatApiError(err, 'Secure checkout is unavailable')));
            }}>Resume secure card checkout</button>
          )}
          {paymentError && <p role="alert" className="mt-2 text-sm text-red-700">{paymentError}</p>}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/account/orders" className="flex items-center justify-center gap-2 bg-[#2d2d2d] text-white px-6 py-3 rounded-lg text-sm">View My Orders</Link>
          <Link to="/shop" className="flex items-center justify-center gap-2 border border-[#e7e3dd] px-6 py-3 rounded-lg text-sm hover:bg-gray-50">Continue Shopping <ArrowRight size={14} /></Link>
        </div>
      </div>
    </div>
  );
}
